import React, { memo, useMemo, useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

import './style.scss';
import useTenantTranslation from 'utils/hooks/useTenantTranslation';
import remarkGfm from 'remark-gfm';

interface EllipsisProps {
  text: string;
  maxLine?: number;
  customStyle?: {
    container: string;
  };
  readMoreText?: string;
  readLessText?: string;
}

const Ellipsis: React.FC<EllipsisProps> = ({
  text,
  maxLine = 2,
  customStyle,
  readMoreText,
  readLessText,
}) => {
  const [isEllipsis, setEllipsis] = useState(true);
  const [showToggle, setShowToggle] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { t } = useTenantTranslation();

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      const computedStyle = window.getComputedStyle(el);
      const lineHeight = parseFloat(computedStyle.lineHeight || '20');
      const totalLines = el.scrollHeight / lineHeight;
      if (totalLines > maxLine) {
        setShowToggle(true);
      }
    }
  }, [text, maxLine]);

  const readMoreBtn = useMemo(
    () => (
      <button
        type="button"
        onClick={() => setEllipsis(false)}
        className="read_more"
      >
        {readMoreText || t('common.read_more')}
      </button>
    ),
    [],
  );

  const readLessBtn = useMemo(
    () => (
      <button
        type="button"
        onClick={() => setEllipsis(true)}
        className="read_less"
      >
        {readLessText || t('common.read_less')}
      </button>
    ),
    [],
  );

  return (
    <div className={clsx('ellipsis', customStyle?.container)}>
      <div
        ref={contentRef}
        className={clsx('ellipsis__content', { 'ellipsis--clamp': isEllipsis })}
        style={
          isEllipsis
            ? {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                WebkitLineClamp: maxLine,
              }
            : undefined
        }
      >
        <ReactMarkdown rehypePlugins={[rehypeRaw]} remarkPlugins={[remarkGfm]}>
          {text}
        </ReactMarkdown>
      </div>
      {showToggle && (isEllipsis ? readMoreBtn : readLessBtn)}
    </div>
  );
};

export default memo(Ellipsis);
